import { useRef } from 'react';
import { motion, useMotionValue, useTransform, useSpring } from 'motion/react';
import { ArrowRight, TrendingUp, CheckCircle, Clock, CalendarCheck } from 'lucide-react';
import { useBookDemoStore } from '../store/book-demo-store';

// ── Floating geometric shape ────────────────────────────────────

function FloatingShape({
  className,
  delay = 0,
  duration = 6,
  rotate = false,
}: {
  className: string;
  delay?: number;
  duration?: number;
  rotate?: boolean;
}) {
  return (
    <motion.div
      className={className}
      animate={{
        y: [0, -18, 0],
        rotate: rotate ? [0, 360] : [0, 0],
      }}
      transition={{
        y: { duration, repeat: Infinity, ease: 'easeInOut', delay },
        rotate: rotate
          ? { duration: duration * 3, repeat: Infinity, ease: 'linear', delay }
          : undefined,
      }}
    />
  );
}

// ── Main hero ───────────────────────────────────────────────────

export function HeroSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const openDemo = useBookDemoStore((s) => s.open);

  // Mouse tracking for 3D tilt
  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);
  const rotateY = useSpring(useTransform(rawX, [-0.5, 0.5], [-12, 12]), {
    stiffness: 80,
    damping: 20,
  });
  const rotateX = useSpring(useTransform(rawY, [-0.5, 0.5], [8, -8]), {
    stiffness: 80,
    damping: 20,
  });
  const shadowX = useTransform(rawX, [-0.5, 0.5], [-20, 20]);
  const shadowY = useTransform(rawY, [-0.5, 0.5], [-10, 10]);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!sectionRef.current) return;
    const rect = sectionRef.current.getBoundingClientRect();
    rawX.set((e.clientX - rect.left) / rect.width - 0.5);
    rawY.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  const handleMouseLeave = () => {
    rawX.set(0);
    rawY.set(0);
  };

  const handleScrollToDemo = (e: React.MouseEvent) => {
    e.preventDefault();
    document.querySelector('#features')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      ref={sectionRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="relative min-h-screen flex items-center overflow-hidden pt-16"
    >
      {/* ── Background video loop (smart pricing strategies) ── */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          className="w-full h-full object-cover opacity-25 dark:opacity-20 scale-110"
          aria-hidden="true"
        >
          <source src="/deck-media/smart-estrategias.webm" type="video/webm" />
        </video>
        {/* Wash overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/85 via-white/70 to-white/95 dark:from-gray-950/90 dark:via-gray-950/75 dark:to-gray-950/95" />
      </div>

      {/* ── Gradient orbs ── */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-1/4 -left-32 w-[500px] h-[500px] bg-blue-400/15 dark:bg-blue-400/10 rounded-full blur-[120px]"
          animate={{ scale: [1, 1.15, 1], x: [0, 30, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute top-1/3 -right-32 w-[500px] h-[500px] bg-violet-400/15 dark:bg-violet-400/10 rounded-full blur-[120px]"
          animate={{ scale: [1, 1.2, 1], x: [0, -30, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
        />
        <motion.div
          className="absolute bottom-1/4 left-1/3 w-72 h-72 bg-cyan-400/10 dark:bg-cyan-400/5 rounded-full blur-[80px]"
          animate={{ scale: [1, 1.3, 1] }}
          transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 4 }}
        />
      </div>

      {/* ── Floating 3D shapes ── */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Cube outline top-left */}
        <FloatingShape
          delay={0}
          duration={7}
          rotate
          className="absolute top-24 left-[8%] w-14 h-14 border-2 border-blue-300/40 dark:border-blue-400/20 rounded-lg"
        />
        {/* Ring top-right */}
        <FloatingShape
          delay={1.5}
          duration={9}
          rotate
          className="absolute top-32 right-[12%] w-20 h-20 border-2 border-violet-300/35 dark:border-violet-400/20 rounded-full"
        />
        {/* Small cube mid-left */}
        <FloatingShape
          delay={3}
          duration={6}
          className="absolute top-1/2 left-[5%] w-8 h-8 border-2 border-cyan-400/40 dark:border-cyan-400/20 rounded-md rotate-12"
        />
        {/* Diamond bottom-right */}
        <FloatingShape
          delay={2}
          duration={8}
          rotate
          className="absolute bottom-32 right-[8%] w-12 h-12 border-2 border-blue-400/30 dark:border-blue-400/15 rounded-sm rotate-45"
        />
        {/* Large ring bottom-left */}
        <FloatingShape
          delay={0.5}
          duration={11}
          rotate
          className="absolute bottom-24 left-[15%] w-24 h-24 border border-violet-300/25 dark:border-violet-400/15 rounded-full"
        />
        {/* Dots cluster */}
        <FloatingShape
          delay={1}
          duration={5}
          className="absolute top-[40%] right-[6%] w-3 h-3 bg-blue-400/40 dark:bg-blue-400/25 rounded-full"
        />
        <FloatingShape
          delay={2.5}
          duration={5.5}
          className="absolute top-[43%] right-[8%] w-2 h-2 bg-violet-400/40 dark:bg-violet-400/25 rounded-full"
        />
        <FloatingShape
          delay={0.8}
          duration={6.5}
          className="absolute top-[38%] right-[4.5%] w-2 h-2 bg-cyan-400/40 dark:bg-cyan-400/25 rounded-full"
        />
      </div>

      {/* ── Content: two columns ── */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: text */}
          <div className="space-y-8">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-blue-200 dark:border-blue-500/30 bg-blue-50 dark:bg-blue-500/10 shadow-sm"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" />
              </span>
              <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                Plataforma #1 de Pricing para Latam
              </span>
            </motion.div>

            {/* Headline */}
            <div className="space-y-2">
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 dark:text-white leading-[1.1]"
              >
                Dejá de perder
                <br />
                ventas.
              </motion.h1>
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-violet-600 to-cyan-600"
              >
                Cotizá rápido
                <br />y con precisión.
              </motion.h1>
            </div>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-lg text-gray-500 dark:text-gray-400 leading-relaxed max-w-lg"
            >
              Reemplazá las hojas de cálculo con modelos de pricing dinámicos, aprobaciones
              automatizadas e inteligencia artificial que potencia cada venta.
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-wrap gap-3"
            >
              <button
                onClick={() => openDemo()}
                className="group flex items-center gap-2 px-7 py-3.5 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 text-white font-semibold text-base hover:from-blue-500 hover:to-violet-500 transition-all shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 dark:hover:shadow-[0_0_30px_rgba(99,102,241,0.4)] hover:-translate-y-0.5"
              >
                <CalendarCheck className="w-4 h-4" />
                Solicitar demo
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={handleScrollToDemo}
                className="flex items-center gap-2 px-7 py-3.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600 transition-all text-base font-medium hover:-translate-y-0.5"
              >
                Ver características
              </button>
            </motion.div>

            {/* Social proof mini */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex items-center gap-6 pt-2"
            >
              {[
                { icon: CheckCircle, label: 'Sin tarjeta de crédito' },
                { icon: Clock, label: 'Listo en 5 minutos' },
                { icon: TrendingUp, label: '15 días de demo guiada' },
              ].map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="flex items-center gap-1.5 text-sm text-gray-400 dark:text-gray-500"
                >
                  <Icon className="w-3.5 h-3.5 text-blue-500" />
                  <span>{label}</span>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right: 3D dashboard mockup */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="hidden lg:block"
            style={{ perspective: '1200px' }}
          >
            <motion.div
              style={{
                rotateX,
                rotateY,
                transformStyle: 'preserve-3d',
              }}
              className="relative"
            >
              {/* Glow shadow that follows tilt */}
              <motion.div
                style={{ x: shadowX, y: shadowY }}
                className="absolute -inset-6 bg-gradient-to-br from-blue-500/20 via-violet-500/15 to-cyan-500/10 dark:from-blue-500/15 dark:via-violet-500/10 dark:to-cyan-500/5 rounded-3xl blur-3xl"
              />

              {/* Browser window */}
              <div className="relative rounded-2xl border border-gray-200/80 dark:border-gray-700/80 bg-white dark:bg-gray-900 shadow-2xl overflow-hidden">
                {/* Title bar */}
                <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                  </div>
                  <div className="flex-1 flex justify-center">
                    <div className="flex items-center gap-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md px-3 py-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                      <span className="text-[11px] text-gray-400 dark:text-gray-500">
                        prixia.app/dashboard
                      </span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5 space-y-4 bg-gray-50/50 dark:bg-gray-800/50">
                  {/* Metric cards */}
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      {
                        label: 'Cotizaciones activas',
                        value: '147',
                        change: '+12%',
                        from: 'from-blue-500',
                        to: 'to-blue-600',
                        light: 'from-blue-50 to-blue-100/50',
                        darkBg: 'dark:from-blue-950/50 dark:to-blue-900/30',
                        text: 'text-blue-600 dark:text-blue-400',
                      },
                      {
                        label: 'Win Rate',
                        value: '68%',
                        change: '+5%',
                        from: 'from-violet-500',
                        to: 'to-violet-600',
                        light: 'from-violet-50 to-violet-100/50',
                        darkBg: 'dark:from-violet-950/50 dark:to-violet-900/30',
                        text: 'text-violet-600 dark:text-violet-400',
                      },
                      {
                        label: 'Revenue Pipeline',
                        value: '$2.4M',
                        change: '+18%',
                        from: 'from-cyan-500',
                        to: 'to-cyan-600',
                        light: 'from-cyan-50 to-cyan-100/50',
                        darkBg: 'dark:from-cyan-950/50 dark:to-cyan-900/30',
                        text: 'text-cyan-600 dark:text-cyan-400',
                      },
                    ].map((card) => (
                      <div
                        key={card.label}
                        className={`rounded-xl bg-gradient-to-br ${card.light} ${card.darkBg} border border-gray-100 dark:border-gray-700 p-3.5`}
                        style={{ transform: 'translateZ(20px)' }}
                      >
                        <div
                          className={`w-6 h-6 rounded-md bg-gradient-to-br ${card.from} ${card.to} mb-2`}
                        />
                        <p className="text-[10px] text-gray-400 dark:text-gray-500 mb-0.5">
                          {card.label}
                        </p>
                        <p className="text-xl font-bold text-gray-900 dark:text-white">
                          {card.value}
                        </p>
                        <p className={`text-[10px] ${card.text} mt-0.5 font-medium`}>
                          {card.change}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* Chart bar mock */}
                  <div
                    className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-4"
                    style={{ transform: 'translateZ(10px)' }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                        Revenue mensual
                      </p>
                      <span className="text-[10px] text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10 px-2 py-0.5 rounded-full font-medium">
                        +24% YoY
                      </span>
                    </div>
                    <div className="flex items-end gap-1.5 h-16">
                      {[40, 55, 45, 70, 60, 85, 75, 90, 80, 95, 88, 100].map((h, i) => (
                        <motion.div
                          key={i}
                          className="flex-1 rounded-sm bg-gradient-to-t from-blue-500 to-violet-500 opacity-80"
                          initial={{ height: 0 }}
                          animate={{ height: `${h}%` }}
                          transition={{ duration: 0.6, delay: 0.8 + i * 0.05, ease: 'easeOut' }}
                        />
                      ))}
                    </div>
                    <div className="flex justify-between mt-1">
                      {['Ene', 'Mar', 'May', 'Jul', 'Sep', 'Nov'].map((m) => (
                        <span key={m} className="text-[9px] text-gray-300 dark:text-gray-600">
                          {m}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Quote list */}
                  <div
                    className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-4"
                    style={{ transform: 'translateZ(15px)' }}
                  >
                    <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2.5">
                      Cotizaciones recientes
                    </p>
                    <div className="space-y-2">
                      {[
                        {
                          name: 'Empresa ABC',
                          amount: '$45,200',
                          status: 'Pendiente',
                          statusColor:
                            'text-yellow-600 bg-yellow-50 dark:text-yellow-400 dark:bg-yellow-500/10',
                        },
                        {
                          name: 'Corp XYZ',
                          amount: '$128,500',
                          status: 'Aprobado',
                          statusColor:
                            'text-green-600 bg-green-50 dark:text-green-400 dark:bg-green-500/10',
                        },
                        {
                          name: 'Industrias SA',
                          amount: '$67,800',
                          status: 'En revisión',
                          statusColor:
                            'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-500/10',
                        },
                      ].map(({ name, amount, status, statusColor }) => (
                        <div
                          key={name}
                          className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <div className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-400 to-violet-400 flex items-center justify-center text-[8px] font-bold text-white">
                              {name[0]}
                            </div>
                            <span className="text-xs text-gray-600 dark:text-gray-400">{name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                              {amount}
                            </span>
                            <span
                              className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${statusColor}`}
                            >
                              {status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Reflection / bottom shadow */}
              <div className="absolute -bottom-8 left-4 right-4 h-8 bg-gradient-to-b from-black/8 to-transparent blur-md rounded-full" />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
